/**
 * Brand gradient icon treatment — the single place that knows HOW to paint an
 * icon in the logo mark's ramp.
 *
 * WHAT GETS IT. Identity icons only: the nav rail, and entity-type glyphs in
 * trees, tables, lists and cards. Utility and affordance icons — close, caret,
 * overflow dots, the composer's paperclip and mic, toolbar search — stay flat
 * ink. This is a deliberate split, not an incomplete rollout: a gradient on
 * every control reads as noise at 14px and costs more legibility than the
 * brand presence is worth.
 *
 * The line between a DECIDER and an opt-in is a criterion, not a census, and it
 * is written that way deliberately: this sentence has carried a wrong count
 * twice. A decider is a component whose CALLERS do not write `gradient=` and
 * which paints it anyway: `entityIcons.ts`, `NavRailIcon`, and `HelpMenu`'s
 * `HelpGlyph`. Everything else opts in explicitly and should be rare.
 * `MobileLayout`'s bottom nav is the largest such opt-in: four glyphs named
 * literally rather than resolved through a registry, each passing `gradient`
 * at the call site, so it sits on the opt-in side however many deciders there
 * turn out to be.
 *
 * THE DEFAULT RULE, one line so a new call site does not have to read the
 * rest: a component that can render ANY glyph (`MaterialIcon`, `BowerIcon`,
 * `Glyph`, `SourceGlyph`) defaults to FLAT and takes `gradient` to opt in; a
 * component that can only ever render an entity-type glyph (`EntityIconTile`,
 * `NavRailIcon`) paints the gradient by default and needs no prop. So doing
 * nothing is always the safe choice, and the opt-in is the same prop name on
 * every component that has one.
 *
 * ONE DELIBERATE EXCLUSION, decided rather than missed: the note glyph in
 * Bower's source list (`MessageSources`, `BirdMessage`) stays flat. It occupies
 * a FAVICON slot — its alternate branch is a remote site's favicon on a
 * `bg.muted` chip — so it is answering "where did this come from?" as a source
 * badge, not "what kind of thing is this?" as an entity glyph. Gradient-ing it
 * would make an internal source look like a different KIND of thing from an
 * external one, which is the opposite of what that list is showing.
 *
 * WHY TWO MECHANISMS. The app ships two icon systems and they are painted in
 * fundamentally different ways:
 *
 *   MaterialIcon  a <span> whose ligature TEXT is the glyph. Paintable with
 *                 `background-clip: text`, which needs `color: transparent`.
 *   BowerIcon     inline SVG whose strokes are `currentColor`. `background-
 *                 clip` does nothing here — SVG paint needs a real
 *                 <linearGradient> element and a `url(#id)` reference.
 *
 * Both read the SAME three stop tokens (`icon.gradientStop1..3`), so light and
 * dark stay in step by construction rather than by remembering to edit twice.
 *
 * TWO ENVIRONMENTS STRIP THE GRADIENT, and in both of them `color:
 * transparent` would leave a blank space where the icon was:
 *
 *   forced-colors  the UA drops background-image entirely. Windows High
 *                  Contrast users would lose every identity icon in the app.
 *   print          backgrounds are commonly not printed (and `PrintLightMode`
 *                  exists precisely because printing is a real surface here).
 *
 * Both fall back to the mid stop as a flat ink. Never hand-roll the
 * background-clip trio at a call site — it is the fallbacks, not the gradient,
 * that are easy to forget, and forgetting them fails silently for exactly the
 * users least able to work around it.
 */

import type { SystemStyleObject } from '@chakra-ui/react';

/** Warm (default) or cool. See `iconGradientSx` for when to pick cool. */
export type IconGradientVariant = 'warm' | 'cool';

/**
 * IDs of the <linearGradient> elements rendered once per document by
 * `<BrandIconGradientDefs />`. An SVG paint reference is document-scoped, so
 * these must be globally unique and must not be suffixed per instance.
 */
export const ICON_GRADIENT_SVG_ID: Readonly<Record<IconGradientVariant, string>> = {
  warm: 'bower-icon-gradient',
  cool: 'bower-icon-gradient-cool',
};

/**
 * Id of the <linearGradient> that `BowerSpinner`'s arc is stroked with.
 *
 * DELIBERATELY NOT a member of `IconGradientVariant`. Adding it there would
 * make `cometary` a legal value for `<MaterialIcon gradient=...>` and for
 * {@link gradientVariantForGlyph}, and it is not an icon treatment: it is one
 * component's paint, and it carries per-stop OPACITY, which no icon gradient
 * does. It lives in this file anyway because this file is what
 * `<BrandIconGradientDefs />` reads to decide which paint servers the document
 * owns, and one list beats two.
 */
export const SPINNER_GRADIENT_SVG_ID = 'bower-spinner-gradient';

/**
 * The flat ink `BowerSpinner` collapses to in print, where paint servers are
 * commonly dropped.
 *
 * The SPINNER's mid stop, not {@link FLAT_FALLBACK}. They differ in light mode
 * (the spinner ramp's mid sits at the 700 step to clear WCAG 1.4.11), and falling
 * back to the icon ramp's would print the arc in the lighter ink the spinner
 * exists not to use.
 */
export const SPINNER_FLAT_FALLBACK = 'var(--chakra-colors-spinner-gradientStop2)';

export const GRADIENT_VAR: Readonly<Record<IconGradientVariant, string>> = {
  warm: 'var(--chakra-colors-icon-gradient)',
  cool: 'var(--chakra-colors-icon-gradientCool)',
};

/**
 * The flat ink both fallbacks collapse to: the gradient's mid stop, which is
 * the accent role in either color mode.
 */
export const FLAT_FALLBACK = 'var(--chakra-colors-icon-gradientStop2)';

/**
 * Glyphs that take the COOL variant instead of the warm default.
 *
 * The warm ramp finishes on the warm stop at the bottom-right. On a magnifying
 * glass that is exactly where the lens is, so the glyph picks up a warm blob on
 * a thin cool stem and reads as a lollipop rather than a search control. The
 * cool variant pushes the warm stop off the corner.
 *
 * Keyed by glyph SHAPE, not by what the control does — a future item that also
 * uses a magnifier belongs here, and a differently-shaped control that happens
 * to search does not. `search_off` is the first case that proved the point: a
 * no-results mark, nothing to do with a search control, same lens in the same
 * corner.
 *
 * It lives in this file rather than in the nav rail because the shape argument
 * has nothing to do with the rail: the same magnifier heads the command
 * palette, and while the rule sat in `NavRailIcon` the two surfaces painted one
 * glyph two different ways. Read it through {@link gradientVariantForGlyph}
 * instead of testing the set directly.
 */
const COOL_GRADIENT_GLYPHS: ReadonlySet<string> = new Set(['search', 'search_off']);

/** Which variant a given glyph should be painted in. */
export function gradientVariantForGlyph(name: string): IconGradientVariant {
  return COOL_GRADIENT_GLYPHS.has(name) ? 'cool' : 'warm';
}

function buildIconGradientSx(variant: IconGradientVariant): SystemStyleObject {
  return {
    backgroundImage: GRADIENT_VAR[variant],
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    '@media (forced-colors: active)': {
      backgroundImage: 'none',
      // `currentColor`, NOT a hardcoded system keyword. `color` is a forced
      // property, so the UA has already resolved the right system color for
      // wherever this glyph sits — ButtonText inside a button, CanvasText on
      // the page. Consumers sit in BOTH places: the rail's buttons and the
      // help menu's items on one side, and rows, table cells, cards and empty
      // states on the other. The contrast themes do not treat the two as
      // interchangeable: Windows Desert pairs ButtonFace #000000 with
      // ButtonText #FFFAEF while CanvasText is #3D3D3D, so hardcoding either
      // keyword paints the wrong ink on one of the two surfaces —
      // `currentColor` is the only value that is right on both.
      //
      // `-webkit-text-fill-color` is NOT a forced property, which is the whole
      // reason this branch exists: without resetting it the glyph stays
      // transparent. Pointing it at `currentColor` makes it follow the color
      // the UA picked instead of second-guessing it.
      color: 'currentColor',
      WebkitTextFillColor: 'currentColor',
    },
    '@media print': {
      backgroundImage: 'none',
      color: FLAT_FALLBACK,
      WebkitTextFillColor: FLAT_FALLBACK,
    },
  };
}

/**
 * The two possible results of {@link iconGradientSx}, built once.
 *
 * There are exactly two variants and the object is deep, nested three levels
 * through its media queries, and never depends on anything but the variant. So
 * the win here is BUILDING it twice at module load instead of once per icon
 * render — on a 50-row table with two glyph columns that is 100 nested literals
 * a render that no longer get constructed.
 *
 * It is NOT a referential-identity win, and saying so would be false: both
 * consumers spread the result into a fresh object (`MaterialIcon` merges the
 * caller's `sx` over it, `BowerIcon` folds it into its own `sx` literal), so
 * Emotion never receives this object itself. Emotion dedupes by serialised
 * content hash anyway, so the class is inserted once regardless.
 */
const ICON_GRADIENT_SX: Readonly<Record<IconGradientVariant, SystemStyleObject>> = Object.freeze({
  warm: buildIconGradientSx('warm'),
  cool: buildIconGradientSx('cool'),
});

/**
 * Gradient fill for a MaterialIcon (ligature text).
 *
 * Pass `cool` for magnifying-glass-shaped glyphs. The warm variant finishes on
 * the warm stop at the bottom-right, which on a magnifier lands squarely on
 * the lens and makes it read as a lollipop; the cool variant pushes the warm
 * stop off the top-left corner so the lens stays blue.
 *
 * Prefer `<MaterialIcon gradient="warm" />` (or `<Glyph gradient="warm" />`)
 * over calling this at a call site: the returned styles only work paired with
 * a suppressed `color`, and the prop is what makes the pair inseparable.
 *
 * The returned object is SHARED across every call site — never mutate it;
 * spread it if you need to add to it.
 */
export function iconGradientSx(variant: IconGradientVariant = 'warm'): SystemStyleObject {
  return ICON_GRADIENT_SX[variant];
}

/**
 * BUILDS the gradient fill for a BowerIcon (inline SVG). Called twice, at module
 * load, to populate {@link ICON_GRADIENT_SVG_SX} — call sites read the frozen
 * pair through {@link iconGradientSvgSx} rather than building their own.
 *
 * The rule it encodes: target the attributes `tintSvg` has already normalised to
 * `currentColor`, rather than every descendant. Selecting `& svg *` would also
 * paint elements carrying `fill="none"` — a CSS property beats a presentation
 * attribute, so a blanket rule turns every unfilled counter into a filled blob.
 * Matching the literal attribute value keeps the override exactly as wide as
 * the tint.
 *
 * The forced-colors and print branches restore `currentColor` / a flat ink for
 * the same reason the MaterialIcon side does: a `url(#…)` paint whose gradient
 * the UA has stripped renders as nothing.
 */
function buildIconGradientSvgSx(variant: IconGradientVariant): SystemStyleObject {
  // The trailing `currentColor` is a FALLBACK COLOUR, not decoration, and it is
  // the difference between degrading and disappearing.
  //
  // SVG's <paint> grammar is `<url> [none | <color>]?`. With the url alone, a
  // reference that does not resolve is treated as `none` — so every identity
  // icon in the tree renders as EMPTY SPACE, silently, with no error anywhere.
  // Verified by deleting the defs from a running page: the entity-icon row lost
  // all twelve glyphs and kept all twelve labels.
  //
  // That is one `<BrandIconGradientDefs />` away at all times: a second React
  // root, a portal into a different document, a test harness, a future entry
  // point. Any of them renders gradient BowerIcons with no paint server, and a
  // comment in main.tsx is not a guarantee. With the fallback the same mistake
  // costs the gradient and keeps the icon.
  const paint = `url(#${ICON_GRADIENT_SVG_ID[variant]}) currentColor`;
  return {
    '& svg [stroke="currentColor"]': { stroke: paint },
    '& svg [fill="currentColor"]': { fill: paint },
    '@media (forced-colors: active)': {
      '& svg [stroke="currentColor"]': { stroke: 'currentColor' },
      '& svg [fill="currentColor"]': { fill: 'currentColor' },
    },
    '@media print': {
      '& svg [stroke="currentColor"]': { stroke: FLAT_FALLBACK },
      '& svg [fill="currentColor"]': { fill: FLAT_FALLBACK },
    },
  };
}

/** Built once, for the same reason as {@link ICON_GRADIENT_SX}. */
const ICON_GRADIENT_SVG_SX: Readonly<Record<IconGradientVariant, SystemStyleObject>> =
  Object.freeze({
    warm: buildIconGradientSvgSx('warm'),
    cool: buildIconGradientSvgSx('cool'),
  });

/**
 * See {@link buildIconGradientSvgSx} above for what these styles do and why.
 * The returned object is SHARED across every call site — never mutate it.
 */
export function iconGradientSvgSx(variant: IconGradientVariant = 'warm'): SystemStyleObject {
  return ICON_GRADIENT_SVG_SX[variant];
}
